import React, { useCallback, useContext } from 'react';
import classes from './PostStatusSelector.module.scss';
import {
  SelectItem,
  SelectItemTemplate,
  SelectValueTemplate,
  SelectViewProps
} from '../../../components/forms/Select/Select.view';
import classNames from 'classnames';
import { Select } from '../../../components/forms/Select';
import { RegisterOptions, UseFormReturn } from 'react-hook-form';
import { ValidationMessages } from '../../../constants/validation-messages';
import { PostStatus } from '../../../models/graphql';
import { ListItem } from '../../../components/actions/ListItem';
import { PostEditorContext } from '../states/PostEditorProvider';
import { PostEditorGroupDataContext } from '../states/PostEditorGroupDataProvider';
import { ActiveCustomizeIndexContext } from '../states/ActiveCustomizeIndexProvider';
import { FormElementAction, FormElementValueType } from '../../../models/basic-objects';
import { useDidMount } from '../../../hooks/useDidMount';

export type PostStatusSelectorViewProps = {
  //
  formInstance: UseFormReturn;
  transparent?: boolean;
  customizeType: 'schedule' | 'content' | 'global';
  readonly?: boolean;
  callback?: FormElementAction<PostStatus>;
  value?: PostStatus;
  disabled?: boolean;
};

const getPostStatusItemsList = (current: PostStatus): SelectItem[] => {
  const items = [
    {
      label: 'Active',
      value: PostStatus.ACTIVE
    },
    {
      label: 'Draft',
      value: PostStatus.DRAFT
    }
  ];
  switch (current) {
    case PostStatus.PUBLISHED:
      return [
        ...items,
        {
          label: 'Past',
          value: PostStatus.PUBLISHED
        }
      ];
    case PostStatus.FAILED:
      return [
        ...items,
        {
          label: 'Failed',
          value: PostStatus.FAILED
        }
      ];
    default:
      return items;
  }
};

export const PostStatusSelectorView = (props: PostStatusSelectorViewProps): JSX.Element => {
  const { formInstance, transparent, customizeType, readonly, value, callback, disabled } = props;
  const { state, dispatch } = useContext(PostEditorContext);
  const [tabsState] = useContext(PostEditorGroupDataContext);
  const { postGroupIndex } = tabsState;
  const postGroup = state.postGroups[postGroupIndex];
  const customizeIndex = useContext(ActiveCustomizeIndexContext);
  const postStatus = value
    ? value
    : readonly
    ? typeof customizeIndex === 'number' && postGroup.readOnlyPosts[customizeIndex]
      ? postGroup.readOnlyPosts[customizeIndex].postStatus
      : PostStatus.ACTIVE
    : typeof customizeIndex === 'number' && postGroup.posts[customizeIndex]
    ? postGroup.posts[customizeIndex].postStatus
    : postGroup.groupPostAttributes.postStatus;
  const formName = typeof customizeIndex === 'number' ? `posts.${customizeIndex}.postStatus` : 'globalPostStatus';
  const rules: RegisterOptions = {
    required: ValidationMessages.select.required
  };

  useDidMount(() => formInstance.setValue(formName, postStatus));

  const items = getPostStatusItemsList(postStatus);

  const valueTemplate: SelectValueTemplate = useCallback(
    (_args: SelectViewProps, globalClasses: string) => {
      const wrapperClasses = classNames(globalClasses, {
        [classes.PostStatusSelectorTransparent]: !!transparent,
        [classes.PostStatusSelectorDisabled]: !!readonly
      });
      const itemClasses = classNames(classes.PostStatusDot, {
        [classes.PostStatusDotActive]: postStatus === PostStatus.ACTIVE && !disabled,
        [classes.PostStatusDotDraft]: postStatus === PostStatus.DRAFT && !disabled,
        [classes.PostStatusDotPast]: postStatus === PostStatus.PUBLISHED && !disabled,
        [classes.PostStatusDotFailed]: postStatus === PostStatus.FAILED && !disabled
      });

      let itemLabel: string;
      switch (postStatus) {
        case PostStatus.ACTIVE:
          itemLabel = 'Active';
          break;
        case PostStatus.DRAFT:
          itemLabel = 'Draft';
          break;
        case PostStatus.PUBLISHED:
          itemLabel = 'Past';
          break;
        case PostStatus.FAILED:
          itemLabel = 'Failed';
          break;
        default:
          itemLabel = 'Unknown';
          break;
      }
      return (
        <div className={wrapperClasses}>
          <div className={'grid-x align-middle'} style={{ pointerEvents: 'none' }}>
            <div className={'cell shrink'}>
              <div className={itemClasses}>
                <span className={'show-for-sr'}>{postStatus}</span>
              </div>
            </div>
            <div className={'cell shrink'}>
              <div className={classes.ValueLabel}>{itemLabel}</div>
            </div>
          </div>
        </div>
      );
    },
    [transparent, readonly, postStatus, disabled]
  );

  const isPostStatusGuard = (value: FormElementValueType): value is PostStatus => {
    if (typeof value !== 'string') throw new Error('value is not of type string');
    return value in PostStatus;
  };

  const updateValueAction: FormElementAction = (value, complete) => {
    if (!isPostStatusGuard(value)) throw new Error('Value is not a PostStatus enum');
    if (callback) {
      callback(value, complete);
    } else {
      if (complete) complete();
      dispatch({
        type: 'setPostStatus',
        payload: {
          postStatus: value,
          customizeIndex,
          postGroupIndex,
          customizeType
        }
      });
    }
  };

  const itemTemplate: SelectItemTemplate = (args, index, item) => {
    const { updateValue } = args;
    const itemClasses = classNames(classes.PostStatusDot, {
      [classes.PostStatusDotActive]: item.value === PostStatus.ACTIVE,
      [classes.PostStatusDotDraft]: item.value === PostStatus.DRAFT,
      [classes.PostStatusDotPast]: item.value === PostStatus.PUBLISHED,
      [classes.PostStatusDotFailed]: item.value === PostStatus.FAILED
    });
    let itemLabel: string;
    switch (item.value) {
      case PostStatus.ACTIVE:
        itemLabel = 'Active';
        break;
      case PostStatus.DRAFT:
        itemLabel = 'Draft';
        break;
      case PostStatus.PUBLISHED:
        itemLabel = 'Past';
        break;
      case PostStatus.FAILED:
        itemLabel = 'Failed';
        break;
      default:
        itemLabel = 'Unknown';
        break;
    }
    return (
      <ListItem
        unsetWidth={true}
        key={index}
        role={'option'}
        aria-label={item.label}
        variation={'regular'}
        type={'li'}
        action={() => {
          updateValue(item.value);
        }}
        disabled={item.value === postStatus}
        selected={false}>
        <div className={classes.ListItem}>
          <div className={'grid-x align-middle'}>
            <div className={'cell shrink'}>
              <div className={itemClasses}>
                <span className={'show-for-sr'}>{item.value}</span>
              </div>
            </div>
            <div className={'cell shrink'}>
              <div className={classes.ValueLabel}>{itemLabel}</div>
            </div>
          </div>
        </div>
      </ListItem>
    );
  };

  return (
    <Select
      disabled={disabled}
      {...(callback ? {} : { dropdownSide: 'right' })}
      selectedValue={postStatus}
      items={items}
      action={updateValueAction}
      useFormInstance={formInstance}
      name={formName}
      size={callback ? 'medium' : 'small'}
      rules={rules}
      itemTemplate={itemTemplate}
      valueTemplate={valueTemplate}
    />
  );
};
